/**
 * E1 — environment-driven site configuration.
 *
 * Per the explicit instruction: "Use environment/configuration so the
 * deployment domain can be changed cleanly" and "Document every location
 * where the domain is referenced" (no domain is hardcoded or silently
 * assumed — see E1_WEBSITE_INTEGRATION_REPORT.md §... for the full
 * inventory). Every value here has a clearly-marked placeholder default
 * so a build without the real env vars set is honest about what's
 * unconfigured, rather than silently shipping a wrong or invented value.
 */

// The canonical public domain this site is deployed at. Used only for
// generating absolute URLs where one is structurally required (canonical
// link tags, structured data, sitemap). NEVER used to build API calls —
// see src/services/api.js, which always talks to REACT_APP_API_URL /
// '/api' instead, entirely independent of this value.
export const SITE_DOMAIN =
  process.env.REACT_APP_SITE_DOMAIN || '[SITE_DOMAIN_NOT_CONFIGURED]';

export const SITE_URL = SITE_DOMAIN.startsWith('[')
  ? SITE_DOMAIN
  : `https://${SITE_DOMAIN}`;

// Legal-entity facts that cannot be verified or invented by this
// integration phase (see E1_WEBSITE_INTEGRATION_REPORT.md's domain/legal
// findings) — each renders visibly as an unresolved placeholder in the
// legal pages rather than silently showing a plausible-looking but
// unverified value.
export const LEGAL_ENTITY_NAME =
  process.env.REACT_APP_LEGAL_ENTITY_NAME || '[LEGAL ENTITY NAME NOT CONFIRMED]';
export const LEGAL_ENTITY_ADDRESS =
  process.env.REACT_APP_LEGAL_ENTITY_ADDRESS || '[REGISTERED ADDRESS NOT CONFIRMED]';
export const LEGAL_JURISDICTION =
  process.env.REACT_APP_LEGAL_JURISDICTION || '[GOVERNING JURISDICTION NOT CONFIRMED]';

// Unlike the legal-entity facts above, this one isn't unconfirmed — it's
// the website's own existing, real contact address (already live on
// shebasmarthome.com, the confirmed canonical domain per CLAUDE.md),
// simply centralized here instead of left hardcoded in ContactSection.js.
export const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || 'info@shebasmarthome.com';
export const PRIVACY_CONTACT_EMAIL =
  process.env.REACT_APP_PRIVACY_EMAIL || SUPPORT_EMAIL;

// E2.2 — where the canonical customer portal (frontend/customer/) is
// deployed. No production deployment target has been decided for either
// this site or the portal (see E2_PHASE0_ARCHITECTURE_PLAN.md §3/§6), so
// this cannot default to a real URL without guessing one. It defaults
// instead to this site's OWN now-superseded dashboard — not a broken
// placeholder, but the one thing that's actually still true today: until
// a real portal URL is configured, the only working destination IS the
// local /dashboard route. Once REACT_APP_CUSTOMER_PORTAL_URL is set (at
// deploy time, once the portal's real location is decided), every call
// site below starts sending users there instead automatically, with no
// further code change. This must never leave a user stranded with a
// broken navigation, which a bracketed "[NOT CONFIGURED]"-style
// placeholder (used elsewhere in this file for content that's safe to
// show as unresolved text) would do here, since this value is navigated
// to, not displayed.
export const CUSTOMER_PORTAL_URL =
  process.env.REACT_APP_CUSTOMER_PORTAL_URL || '/dashboard';
